import useChallengesQuery from "@hooks/common/challenges/use-challenges-query";
import BybButtonWrapper from "@Components/common/byb-ds/buttons/byb-button-wrapper";
import { ChallengeTag } from "@Containers/game/challenges/types";
import ProgressBar from "@Containers/shared/cards/components/progress-bar";
import { Events } from "@Context/common/general/context";
import { useGlobalContext } from "@hooks/index";
import { Box, ButtonProps, capitalize, Stack, Typography, useTheme } from "@mui/material";
import { isString } from "lodash-es";

interface BybCategoryButtonProps extends ButtonProps {
  tag?: ChallengeTag;
  height?: number;
}

/**
 *
 * @param tag - The Challenge tag for the category page that this button will navigate to. If provided, onClick is not needed and a function call will be made to fetch the challenges for the given category (to be displayed on the button).
 * @param height - the height of the squircle border (VR)
 * @returns A button that should be used to navigate to a Challenge category page.
 */
const BybCategoryButton = ({ tag, height, children, ...props }: BybCategoryButtonProps) => {
  const { globalState } = useGlobalContext();
  const theme = useTheme();
  const { id: tagId, color: tagColor } = tag || {};

  const { challenges } = useChallengesQuery(
    {
      include_tags: tagId ? [tagId] : undefined,
      exclude_tags: !globalState.user.isSuperAdmin ? ["QA", "DEVELOPMENT"] : [],
      unpublished_allowed: globalState.user.isSuperAdmin,
    },
    { enabled: Boolean(tagId) }
  );
  const completedChallenges = challenges.filter(challenge => challenge.status === "COMPLETED").length;
  const isCompleted = challenges.length > 0 && challenges.length === completedChallenges;
  const isInProgress = completedChallenges > 0;
  const color = isCompleted ? theme.palette.dsPrimaryGradient.green : isInProgress ? theme.palette.dsNeutral.v300 : theme.palette.dsNeutral.v200;

  const onCategoryClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    props.onClick?.(event) || Events.app.navigate.emit({ path: `/challenges/category/${tagId || "all"}` });
  };

  return (
    <BybButtonWrapper
      sx={{
        backgroundColor: "dsSurface.v100",
        color: "dsBase.v200",
        padding: 0,
        height: "100%",
        ":hover": {
          backgroundColor: "dsSurface.v200",
        },
        ...(tagColor && {
          outline: "2px solid",
          outlineColor: tagColor,
        }),
      }}
      onClick={onCategoryClick}
      {...props}
    >
      <Stack sx={{ padding: theme.spacing(3, 2, 2), width: "100%" }} spacing={0.5}>
        <Typography variant="text1xl500" color="dsBase.v200" sx={{ textTransform: "none" }}>
          {isString(children) ? capitalize(children) : children}
        </Typography>
        {tag ? (
          <Stack
            direction="row"
            spacing={1}
            sx={{
              width: "100%",
              alignItems: "center",
            }}
          >
            <ProgressBar value={challenges.length ? (completedChallenges / challenges.length) * 100 : 0} barStyle={{ background: color }} />
            <Typography variant="textLg600" sx={isCompleted ? { backgroundImage: color, color: "transparent", backgroundClip: "text" } : { color }}>
              {`${completedChallenges}/${challenges.length}`}
            </Typography>
          </Stack>
        ) : (
          <Box sx={{ height: theme.spacing(0.5) }} />
        )}
      </Stack>
    </BybButtonWrapper>
  );
};

export default BybCategoryButton;
